import { AttractionTypeDropdown } from '@/components/dropdowns';
import type { Attraction } from '@/config/objects/attractions';
import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Loading,
  toast,
  useApiClient,
} from '@startupquickstart/frontend';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { MapPin, Search } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

interface ReassignAttractionsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The type whose attractions are being moved. */
  sourceTypeId: string;
  sourceName: string;
  /** Called after a successful reassignment so the caller can refetch. */
  onReassigned?: () => void;
}

interface AttractionsResponse {
  success: boolean;
  data: Attraction[];
}

/**
 * Move a chosen subset of a type's attractions to another type. Lists the
 * source type's attractions with checkboxes and a target-type picker; on submit
 * the selected attractions get the target type and lose the source type.
 */
export function ReassignAttractionsDialog({
  open,
  onOpenChange,
  sourceTypeId,
  sourceName,
  onReassigned,
}: ReassignAttractionsDialogProps) {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [targetTypeId, setTargetTypeId] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Reset every time the dialog opens so prior state never leaks.
  useEffect(() => {
    if (open) {
      setSearch('');
      setSelectedIds(new Set());
      setTargetTypeId('');
      setSubmitting(false);
    }
  }, [open]);

  const { data, isLoading } = useQuery<AttractionsResponse>({
    queryKey: ['AttractionTypeAttractions', sourceTypeId],
    queryFn: () =>
      apiClient.get(`/attraction-types/${sourceTypeId}/attractions`, {
        params: { page: 1, limit: 200, orderBy: { name: 'asc' } },
      }),
    enabled: open && !!sourceTypeId,
  });

  const attractions = data?.data ?? [];

  const candidates = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return attractions;
    return attractions.filter((a) => a.name?.toLowerCase().includes(term));
  }, [attractions, search]);

  const selectedCount = selectedIds.size;
  const canSubmit = selectedCount > 0 && !!targetTypeId && targetTypeId !== sourceTypeId;

  const toggle = (attractionId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(attractionId)) next.delete(attractionId);
      else next.add(attractionId);
      return next;
    });
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;
    const attractionIds = [...selectedIds];
    setSubmitting(true);
    try {
      await toast.promise(
        apiClient
          .post<{ success: boolean; error?: string }>(
            `/attraction-types/${sourceTypeId}/reassign-attractions`,
            { targetTypeId, attractionIds },
          )
          .then((res) => {
            if (!res.success) throw new Error(res.error || 'Failed to move attractions');
          }),
        {
          loading: 'Moving attractions...',
          success: `Moved ${attractionIds.length} attraction(s) to the target type.`,
          error: (error: Error) => error?.message || 'Failed to move attractions',
        },
      );
      queryClient.invalidateQueries({ queryKey: ['Attraction'] });
      queryClient.invalidateQueries({ queryKey: ['AttractionTypeAttractions', sourceTypeId] });
      onReassigned?.();
      onOpenChange(false);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onOpenChange(false)}>
      <DialogContent className="flex max-h-[80vh] flex-col overflow-hidden p-0 sm:max-w-[600px]">
        <DialogHeader className="px-6 pb-3 pt-6">
          <DialogTitle>Move attractions to another type</DialogTitle>
          <DialogDescription>
            Selected attractions will be moved off <strong>{sourceName}</strong> and
            onto the chosen type.
          </DialogDescription>
          <div className="relative mt-3">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search attractions..."
              className="pl-9"
              autoFocus
            />
          </div>
        </DialogHeader>

        <div className="min-h-0 flex-1 space-y-2 overflow-y-auto px-6 py-3">
          {isLoading && <Loading text="Loading attractions..." size="sm" />}
          {!isLoading && candidates.length === 0 && (
            <div className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground">
              {search.trim()
                ? `No attractions match "${search.trim()}".`
                : 'This type has no attractions.'}
            </div>
          )}
          {candidates.map((attraction) => {
            const checked = selectedIds.has(attraction.id);
            return (
              <div
                key={attraction.id}
                role="button"
                tabIndex={0}
                aria-pressed={checked}
                onClick={() => toggle(attraction.id)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    toggle(attraction.id);
                  }
                }}
                className={`flex w-full cursor-pointer items-center gap-3 rounded-md border p-3 text-left transition-colors hover:bg-accent focus:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                  checked ? 'border-primary bg-accent/40' : ''
                }`}
              >
                <Checkbox
                  checked={checked}
                  tabIndex={-1}
                  aria-label={`Select ${attraction.name}`}
                />
                <div className="flex h-10 w-14 shrink-0 items-center justify-center rounded bg-muted">
                  <MapPin className="h-4 w-4 text-muted-foreground" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium">{attraction.name}</div>
                  {(attraction.city || attraction.state) && (
                    <div className="truncate text-xs text-muted-foreground">
                      {[attraction.city, attraction.state].filter(Boolean).join(', ')}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <DialogFooter className="flex-col items-stretch gap-3 border-t px-6 py-4">
          <div>
            <label className="mb-1.5 block text-sm font-medium">Target type</label>
            <AttractionTypeDropdown
              value={targetTypeId}
              onChange={setTargetTypeId}
              placeholder="Select a type to move to"
            />
          </div>
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div className="text-sm text-muted-foreground">
              {selectedCount > 0 ? `${selectedCount} selected` : 'Select one or more attractions'}
            </div>
            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
                Cancel
              </Button>
              <Button onClick={handleSubmit} disabled={!canSubmit || submitting} loading={submitting}>
                {selectedCount > 0 ? `Move ${selectedCount}` : 'Move'}
              </Button>
            </div>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
